import { useEffect, useState, useCallback } from "react";
import { api, fmt, fmtDec, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, EmptyRow } from "@/components/shared";
import { toast } from "sonner";
import { Plus, Trash2, Wheat, TrendingDown, AlertTriangle, Gauge } from "lucide-react";

export default function Feed() {
  const { coops, myCoops, coopName, selectedCoop } = useApp();
  const activeCoops = myCoops.filter((c) => c.type === "coop");
  const [tab, setTab] = useState("purchase");
  const [purchases, setPurchases] = useState([]);
  const [consumption, setConsumption] = useState([]);
  const [stock, setStock] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [pForm, setPForm] = useState({ coop_id: "", date: new Date().toISOString().slice(0, 10), supplier: "", feed_type: "", quantity_kg: "", silo: "", waybill_no: "", unit_price: "" });
  const [cForm, setCForm] = useState({ coop_id: "", date: new Date().toISOString().slice(0, 10), quantity_kg: "", silo: "" });

  const load = useCallback(async () => {
    const q = selectedCoop !== "all" ? `?coop_id=${selectedCoop}` : "";
    const [p, c, s, mt] = await Promise.all([
      api.get(`/feed/purchases${q}`), api.get(`/feed/consumption${q}`), api.get(`/feed/stock`), api.get(`/feed/metrics`),
    ]);
    setPurchases(p.data); setConsumption(c.data); setStock(s.data); setMetrics(mt.data);
  }, [selectedCoop]);
  useEffect(() => { load(); }, [load]);

  const stockMap = Object.fromEntries(stock.map((s) => [s.coop_id, s.quantity_kg]));

  const savePurchase = async () => {
    if (!pForm.coop_id) return toast.error("Kümes seçin");
    try {
      await api.post("/feed/purchases", { ...pForm, quantity_kg: Number(pForm.quantity_kg || 0), unit_price: Number(pForm.unit_price || 0) });
      toast.success("Yem girişi kaydedildi");
      setPForm({ ...pForm, supplier: "", feed_type: "", quantity_kg: "", silo: "", waybill_no: "", unit_price: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const saveConsumption = async () => {
    if (!cForm.coop_id) return toast.error("Kümes seçin");
    try {
      await api.post("/feed/consumption", { ...cForm, quantity_kg: Number(cForm.quantity_kg || 0) });
      toast.success("Yem tüketimi kaydedildi");
      setCForm({ ...cForm, quantity_kg: "", silo: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Yem Takip & Stok Yönetimi" subtitle="Yem alımları, günlük tüketim ve kritik stok takibi" />

      {/* stock cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {activeCoops.map((c) => {
          const qty = stockMap[c.id] || 0;
          const low = c.feed_threshold_kg > 0 && qty < c.feed_threshold_kg;
          return (
            <div key={c.id} className="rounded-xl border border-border bg-card p-5" data-testid={`feed-stock-${c.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">{c.name}</div>
                <Wheat size={18} className={low ? "text-destructive" : "text-primary"} />
              </div>
              <div className="mt-2 font-head text-2xl font-bold tabular-nums">{fmtDec(qty)} <span className="text-sm font-normal text-muted-foreground">kg</span></div>
              {low && <div className="mt-2 flex items-center gap-1.5 text-xs text-destructive font-medium"><AlertTriangle size={13} /> Kritik stok (eşik {fmtDec(c.feed_threshold_kg)} kg)</div>}
            </div>
          );
        })}
      </div>

      {metrics.length > 0 && (
        <div className="rounded-xl border border-border bg-card overflow-hidden mb-6" data-testid="feed-metrics">
          <div className="px-5 py-3 border-b border-border flex items-center gap-2">
            <Gauge size={16} className="text-primary" />
            <h3 className="font-head font-semibold text-sm">Yem Verimlilik & Maliyet (Kümes Karşılaştırmalı)</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-secondary/60 text-left">
                  {["Kümes", "Toplam Yumurta", "Tüketilen Yem (kg)", "FCR", "Yem/Yumurta (g)", "Ort. Fiyat (₺/kg)", "Yumurta Başı Maliyet (₺)", "Toplam Yem Maliyeti (₺)"].map((h, i) => (
                    <th key={i} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {metrics.map((m) => (
                  <tr key={m.coop_id} className="border-t border-border hover:bg-secondary/30" data-testid={`metric-row-${m.coop_id}`}>
                    <Td className="font-medium">{m.coop_name}</Td>
                    <Td className="text-right tabular-nums">{fmt(m.total_eggs)}</Td>
                    <Td className="text-right tabular-nums">{fmtDec(m.feed_kg)}</Td>
                    <Td className="text-right tabular-nums font-semibold">{fmtDec(m.fcr)}</Td>
                    <Td className="text-right tabular-nums">{fmtDec(m.feed_per_egg_g)}</Td>
                    <Td className="text-right tabular-nums">{fmtDec(m.avg_price_per_kg)}</Td>
                    <Td className="text-right tabular-nums font-semibold text-primary">{fmtDec(m.cost_per_egg)}</Td>
                    <Td className="text-right tabular-nums">{fmtDec(m.total_feed_cost)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <TabBtn active={tab === "purchase"} onClick={() => setTab("purchase")} testid="feed-tab-purchase">Yem Girişi</TabBtn>
        <TabBtn active={tab === "consumption"} onClick={() => setTab("consumption")} testid="feed-tab-consumption">Günlük Tüketim</TabBtn>
      </div>

      {tab === "purchase" ? (
        <>
          <div className="rounded-xl border border-border bg-card p-5 mb-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 items-end" data-testid="feed-purchase-form">
            <F label="Kümes"><Sel value={pForm.coop_id} onChange={(v) => setPForm({ ...pForm, coop_id: v })} opts={activeCoops} testid="fp-coop" /></F>
            <F label="Tarih"><In type="date" value={pForm.date} onChange={(v) => setPForm({ ...pForm, date: v })} testid="fp-date" /></F>
            <F label="Tedarikçi"><In value={pForm.supplier} onChange={(v) => setPForm({ ...pForm, supplier: v })} testid="fp-supplier" /></F>
            <F label="Yem Tipi/Rasyon"><In value={pForm.feed_type} onChange={(v) => setPForm({ ...pForm, feed_type: v })} testid="fp-type" /></F>
            <F label="Miktar (kg)"><In type="number" value={pForm.quantity_kg} onChange={(v) => setPForm({ ...pForm, quantity_kg: v })} testid="fp-qty" /></F>
            <F label="Birim Fiyat (₺/kg)"><In type="number" value={pForm.unit_price} onChange={(v) => setPForm({ ...pForm, unit_price: v })} testid="fp-price" /></F>
            <F label="Silo/Depo"><In value={pForm.silo} onChange={(v) => setPForm({ ...pForm, silo: v })} testid="fp-silo" /></F>
            <button onClick={savePurchase} data-testid="fp-save" className="h-9 inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-4 text-sm font-semibold hover:bg-primary/90 transition-colors"><Plus size={16} /> Ekle</button>
          </div>
          <TableCard headers={["Tarih", "Kümes", "Tedarikçi", "Yem Tipi", "Miktar(kg)", "Birim Fiyat", "Tutar(₺)", "Silo", ""]}>
            {purchases.length === 0 ? <EmptyRow colSpan={9} /> : purchases.map((p) => (
              <tr key={p.id} className="border-t border-border hover:bg-secondary/30" data-testid={`fp-row-${p.id}`}>
                <Td>{p.date}</Td><Td>{coopName(p.coop_id)}</Td><Td>{p.supplier}</Td><Td>{p.feed_type}</Td>
                <Td className="text-right tabular-nums font-medium">{fmtDec(p.quantity_kg)}</Td>
                <Td className="text-right tabular-nums">{fmtDec(p.unit_price)}</Td>
                <Td className="text-right tabular-nums">{fmtDec((p.quantity_kg || 0) * (p.unit_price || 0))}</Td>
                <Td>{p.silo}</Td>
                <Td><DelBtn onClick={async () => { await api.delete(`/feed/purchases/${p.id}`); load(); }} /></Td>
              </tr>
            ))}
          </TableCard>
        </>
      ) : (
        <>
          <div className="rounded-xl border border-border bg-card p-5 mb-6 grid grid-cols-2 md:grid-cols-4 gap-3 items-end" data-testid="feed-consumption-form">
            <F label="Kümes"><Sel value={cForm.coop_id} onChange={(v) => setCForm({ ...cForm, coop_id: v })} opts={activeCoops} testid="fc-coop" /></F>
            <F label="Tarih"><In type="date" value={cForm.date} onChange={(v) => setCForm({ ...cForm, date: v })} testid="fc-date" /></F>
            <F label="Miktar (kg)"><In type="number" value={cForm.quantity_kg} onChange={(v) => setCForm({ ...cForm, quantity_kg: v })} testid="fc-qty" /></F>
            <button onClick={saveConsumption} data-testid="fc-save" className="h-9 inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-4 text-sm font-semibold hover:bg-primary/90 transition-colors"><Plus size={16} /> Ekle</button>
          </div>
          <TableCard headers={["Tarih", "Kümes", "Miktar(kg)", "Gram/Tavuk/Gün", ""]}>
            {consumption.length === 0 ? <EmptyRow colSpan={5} /> : consumption.map((c) => (
              <tr key={c.id} className="border-t border-border hover:bg-secondary/30" data-testid={`fc-row-${c.id}`}>
                <Td>{c.date}</Td><Td>{coopName(c.coop_id)}</Td>
                <Td className="text-right tabular-nums font-medium">{fmtDec(c.quantity_kg)}</Td>
                <Td className="text-right tabular-nums"><span className="inline-flex items-center gap-1"><TrendingDown size={13} className="text-amber-500" />{fmtDec(c.gram_per_chicken)} g</span></Td>
                <Td><DelBtn onClick={async () => { await api.delete(`/feed/consumption/${c.id}`); load(); }} /></Td>
              </tr>
            ))}
          </TableCard>
        </>
      )}
    </div>
  );
}

const TabBtn = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{children}</button>
);
export const F = ({ label, children }) => (<div><label className="block text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1.5">{label}</label>{children}</div>);
export const In = ({ type = "text", value, onChange, testid, min = "0" }) => (<input type={type} min={type === "number" ? min : undefined} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:ring-2 focus:ring-primary outline-none" />);
export const Sel = ({ value, onChange, opts, testid }) => (<select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:ring-2 focus:ring-primary outline-none"><option value="">Seçin</option>{opts.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select>);
export const DelBtn = ({ onClick }) => (<button onClick={onClick} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={16} /></button>);
export const Td = ({ children, className = "" }) => <td className={`px-3 py-2.5 whitespace-nowrap text-sm ${className}`}>{children}</td>;
export const TableCard = ({ headers, children }) => (
  <div className="rounded-xl border border-border bg-card overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead><tr className="bg-secondary/60 text-left">{headers.map((h, i) => <th key={i} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  </div>
);
